"use client";

import { cn } from "./utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const selectTriggerVariants = cva(
    "flex justify-between items-center bg-white dark:bg-slate-900 disabled:opacity-50 px-3 border border-slate-300 dark:border-slate-500 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-300 ring-offset-white dark:ring-offset-indigo-950 w-full text-slate-800 dark:placeholder:text-slate-400 dark:text-slate-200 text-sm [&>span]:line-clamp-1 disabled:cursor-not-allowed",
    {
        variants: {
            size: {
                sm: "h-10 gap-1 py-0",
                md: "h-12 gap-2 py-2",
            },
        },
        defaultVariants: {
            size: "md",
        },
    }
);

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
        VariantProps<typeof selectTriggerVariants> & {
            testId?: string;
        }
>(({ className, children, testId, size, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        data-testid={testId}
        className={cn(selectTriggerVariants({ size }), className)}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ChevronDown className="opacity-75 w-4 h-4" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn("flex justify-center items-center py-1 cursor-default", className)}
        {...props}
    >
        <ChevronUp size={20} />
    </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn("flex justify-center items-center py-1 cursor-default", className)}
        {...props}
    >
        <ChevronDown size={20} />
    </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
    const dedupedChildren = React.useMemo(() => dedupeSelectItemChildren(children), [children]);

    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                ref={ref}
                className={cn(
                    "data-[side=left]:slide-in-from-right-2 data-[side=top]:slide-in-from-bottom-2 z-50 relative bg-white dark:bg-slate-900 data-[side=bottom]:slide-in-from-top-2 data-[side=right]:slide-in-from-left-2 shadow-md border border-indigo-200 dark:border-slate-500 rounded-md min-w-[8rem] max-h-96 overflow-hidden text-slate-950 dark:text-slate-50 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                    position === "popper" &&
                        "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
                    className
                )}
                position={position}
                {...props}
            >
                <SelectScrollUpButton />
                <SelectPrimitive.Viewport
                    className={cn(
                        "p-1",
                        position === "popper" &&
                            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
                    )}
                >
                    {dedupedChildren}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label ref={ref} className={cn("py-1.5 pr-2 pl-8 font-semibold text-sm", className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cn(
            "relative flex items-center focus:bg-indigo-100 dark:focus:bg-indigo-800 data-[disabled]:opacity-50 py-1.5 pr-2 pl-8 rounded-sm outline-none w-full focus:text-slate-900 dark:focus:text-slate-50 text-sm cursor-default data-[disabled]:pointer-events-none select-none",
            className
        )}
        {...props}
    >
        <span className="left-2 absolute flex justify-center items-center w-3.5 h-3.5">
            <SelectPrimitive.ItemIndicator>
                <Check size={20} />
            </SelectPrimitive.ItemIndicator>
        </span>

        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cn("bg-indigo-100 dark:bg-slate-800 -mx-1 my-1 h-px", className)}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

function isRadixSelectItem(element: React.ReactElement): boolean {
    const type = element.type as { displayName?: string };
    return type?.displayName === SelectPrimitive.Item.displayName;
}

interface SelectChildProps {
    value?: string;
    children?: React.ReactNode;
}

/** Drop duplicate SelectItem children by `value` (first occurrence wins). */
function dedupeSelectItemChildren(children: React.ReactNode, seenValues = new Set<string>()): React.ReactNode {
    return React.Children.map(children, (child) => {
        if (child == null || typeof child === "boolean") {
            return null;
        }

        if (!React.isValidElement<SelectChildProps>(child)) {
            return child;
        }

        if (isRadixSelectItem(child)) {
            const value = child.props.value;
            if (value == null || value === "") {
                return child;
            }
            const key = String(value);
            if (seenValues.has(key)) {
                return null;
            }
            seenValues.add(key);
            return child;
        }

        if (child.props.children != null) {
            const deduped = dedupeSelectItemChildren(child.props.children, seenValues);
            return React.cloneElement(child, undefined, deduped);
        }

        return child;
    });
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
    selectTriggerVariants,
};
